import { NullEngine } from '@babylonjs/core/Engines/nullEngine';
import { Matrix, Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { TraceScene } from '../../core/TraceScene';
import type { SceneObject } from '../../core/SceneObject';
import { Properties } from '../../behaviors/properties';
import { getBehaviorConstructor } from '../../behaviors/BehaviorRegistry';
import { getBehaviorSchema } from '../../scenedata/schemas';

/** A `src` the copy would have to fetch over the network (hosted URL or a server path), as opposed to bytes the scene already holds. */
const NEEDS_FETCH = /^(\/|[a-z][a-z0-9+.-]*:)/i;
const isFetched = (src: string | undefined): boolean => !!src && NEEDS_FETCH.test(src) && !/^(data|blob):/i.test(src);

export interface NativeCopyOptions {
    /**
     * Do not fetch remote model sources: the copy gets a box of the same bounds where the loaded model stood, so a failing asset costs
     * nothing and a loaded one still occupies its space. For reviews, which look at layout and behaviour, never at geometry.
     */
    placeholderRemote?: boolean;
}

/** Local-space bounds of the meshes an object owns, as a centre and size, or null when it owns none. */
function localBounds(object: SceneObject): { center: Vector3; size: Vector3 } | null {
    const meshes = object.getOwnedMeshes().filter(m => m.getTotalVertices() > 0);
    if (!meshes.length) return null;
    const inverse = Matrix.Invert(object.transform.computeWorldMatrix(true));
    const min = new Vector3(Infinity, Infinity, Infinity), max = new Vector3(-Infinity, -Infinity, -Infinity);
    for (const mesh of meshes) {
        mesh.computeWorldMatrix(true);
        for (const corner of mesh.getBoundingInfo().boundingBox.vectorsWorld) {
            const local = Vector3.TransformCoordinates(corner, inverse);
            min.minimizeInPlace(local);
            max.maximizeInPlace(local);
        }
    }
    return { center: min.add(max).scale(0.5), size: max.subtract(min) };
}

/** Copy native authored data without invoking the source export's transform capture. */
export async function copyNativeRuntime(source: TraceScene, opts: NativeCopyOptions = {}) {
    const root = source.getSceneRoot();
    if (!root) throw new Error('Native inspection requires an initialized scene');
    const originals = [root, ...root.getDescendants()];
    const records = originals.map(object => {
        const data = object.getSceneObjectData();
        data.behaviors = object.serializeBehaviors();
        for (const behavior of data.behaviors) {
            const type = behavior['@type'];
            if (!getBehaviorConstructor(type) || !getBehaviorSchema(type) || source.scriptRegistry.isScriptType(type)) {
                throw new Error(`Native inspection cannot execute script or unknown behavior "${type}"; use the isolated browser runtime`);
            }
        }
        const rest = object.getBehavior(Properties)?.getAuthoredTransform();
        const rotation = rest?.rotation ?? object.transform.rotationQuaternion ?? Quaternion.FromEulerVector(object.transform.rotation);
        data.transform = {
            ...data.transform,
            translation: (rest?.position ?? object.transform.position).asArray(),
            rotation: rotation.asArray(),
            scale: (rest?.scale ?? object.transform.scaling).asArray(),
        };
        return data;
    });
    const stubbed = new Set<SceneObject>(opts.placeholderRemote ? originals.filter(o => isFetched(o.src) && !o.isSceneReference) : []);
    const stubBounds = new Map([...stubbed].map(o => [o, localBounds(o)] as const));
    const engine = new NullEngine();
    const trace = new TraceScene({
        engine, canvas: document.createElement('canvas'), traceViewer: new EventTarget(),
        config: { src: null, color: null, prewarm: 0, cameraRadius: 1, blobGLB: null, playMode: false, groundShadow: false, environmentImage: '' },
    });
    const dispose = () => { trace.dispose(); engine.dispose(); };
    try {
        const from = source.getCamera(), camera = trace.getCamera();
        if (from && camera) {
            camera.useFramingBehavior = false;
            camera.upVector = from.upVector.clone();
            camera.setTarget(from.target.clone(), false, true, true);
            for (const key of ['alpha', 'beta', 'radius', 'fov', 'fovMode', 'mode', 'minZ', 'maxZ',
                'orthoLeft', 'orthoRight', 'orthoTop', 'orthoBottom', 'lowerAlphaLimit', 'upperAlphaLimit',
                'lowerBetaLimit', 'upperBetaLimit', 'lowerRadiusLimit', 'upperRadiusLimit', 'allowUpsideDown'] as const) {
                Object.assign(camera, { [key]: from[key] });
            }
            camera.position.copyFrom(from.position);
        }
        // Nested source loads must not compile scripts either.
        trace.scriptRegistry.register = () => { throw new Error('Scripts require the isolated browser runtime'); };
        for (const [name, blob] of source.resources.getAssetEntries()) trace.resources.addAsset(name, blob);
        await trace.startScene();
        const copy = new Map<SceneObject, SceneObject>([[root, trace.getSceneRoot()!]]);
        trace.getSceneRoot()!.name = root.name;
        for (let i = 1; i < originals.length; i++) {
            const original = originals[i];
            const parent = copy.get(original.getParent()!);
            if (!parent) throw new Error('Native inspection requires a connected scene hierarchy');
            // Restore existing derived children; authoring a new child here is correctly vetoed.
            // A reference's content is already in originals. Do not fetch its src again.
            const target = await trace._restoreSceneObjectFromData({
                ...records[i], src: original.isSceneReference || stubbed.has(original) ? '' : records[i].src, behaviors: [],
            }, parent);
            target._setSceneRefInternal(original.isSceneReference);
            target._standInFileSrc = original._standInFileSrc;
            if (!stubbed.has(original)) target._setSrcInternal(original.src);
            copy.set(original, target);
        }
        // Resolve cross-object bindings only after every object exists. Children first: a Layout solves the moment it attaches, so
        // a parent set before its children hold their LayoutItem would flow an Absolute child to the corner and overwrite its transform.
        for (let i = originals.length - 1; i >= 0; i--) {
            const target = copy.get(originals[i])!;
            const record = records[i];
            if (target.getParent()) record.transform!.parent = target.getParent()!.getPath();
            else delete record.transform!.parent;
            if (stubbed.has(originals[i])) record.src = '';
            await target.setData(record);
            const bounds = stubBounds.get(originals[i]);
            if (bounds) {
                const box = MeshBuilder.CreateBox(`${target.name} (stand-in)`, { width: Math.max(bounds.size.x, 1e-4), height: Math.max(bounds.size.y, 1e-4), depth: Math.max(bounds.size.z, 1e-4) }, trace.getBabylonScene());
                box.parent = target.transform;
                box.position.copyFrom(bounds.center);
            }
        }
        return { trace, copy, dispose };
    } catch (error) { dispose(); throw error; }
}
